import "./QuickBooksCallback.scss";

import { KaboodleToaster } from "components/common";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCompleteOAuthFlowMutation,
  useReconnectOAuthFlowMutation,
} from "services/quickbooks-api";
import { useToken } from "utils/context";

// localStorage keys - keep in sync with useQuickBooksReconnect.ts
const QB_RECONNECT_FLAG = "qbIsReconnecting";
const QB_RETURN_PATH = "qbReconnectReturnTo";

/**
 * QuickBooksCallback component that handles OAuth flow completion for QuickBooks.
 * supports both initial connection and reconnection flows.
 */
const QuickBooksCallback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useToken();
  const [completeOAuthFlow] = useCompleteOAuthFlowMutation();
  const [reconnectOAuthFlow] = useReconnectOAuthFlowMutation();
  const hasProcessedRef = useRef(false);

  // handle the OAuth callback
  useEffect(() => {
    // prevent this effect from running twice
    if (hasProcessedRef.current || !accessToken) return;

    const handleCallback = async () => {
      hasProcessedRef.current = true;

      // check localStorage for reconnect flag
      const isReconnectFlow =
        localStorage.getItem(QB_RECONNECT_FLAG) === "true";

      // get authorization code and realm ID from URL
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const realmId = params.get("realmId");

      if (!code || !realmId) {
        KaboodleToaster.error("Invalid QuickBooks callback parameters");
        cleanupAndNavigateBack();
        return;
      }

      try {
        const authParams = {
          authorizationCode: code,
          realmId: realmId,
          accessToken,
        };

        if (isReconnectFlow) {
          await reconnectOAuthFlow(authParams).unwrap();
          KaboodleToaster.success("Successfully reconnected to QuickBooks");
        } else {
          await completeOAuthFlow(authParams).unwrap();
          KaboodleToaster.success("Successfully connected to QuickBooks");
        }

        cleanupAndNavigateBack();
      } catch (error) {
        KaboodleToaster.error("Failed to connect with QuickBooks");
        cleanupAndNavigateBack();
      }
    };

    const cleanupAndNavigateBack = () => {
      // always clean up the reconnect flag
      localStorage.removeItem(QB_RECONNECT_FLAG);

      // navigate back to the stored location
      const returnTo = localStorage.getItem(QB_RETURN_PATH);

      if (returnTo && returnTo.length > 0) {
        localStorage.removeItem(QB_RETURN_PATH);

        // add a small delay to ensure the toast is visible
        setTimeout(() => {
          navigate(returnTo);
        }, 800);
      } else {
        // default redirect to settings
        navigate("/settings");
      }
    };

    handleCallback();
  }, [accessToken, location, history, completeOAuthFlow, reconnectOAuthFlow]);

  // for the UI, check if we're in a reconnect flow
  const isReconnectFlow = localStorage.getItem(QB_RECONNECT_FLAG) === "true";

  return (
    <div className="quickbooks-callback">
      <h2>
        {isReconnectFlow ? "Reconnecting to" : "Connecting with"} QuickBooks
      </h2>
      <p>Please wait while we complete this process...</p>
    </div>
  );
};

export default QuickBooksCallback;
